import { LORAX, SLINKY } from 'HubStyleTokens/colors';
import { CARD_PADDING_COMPACT } from 'HubStyleTokens/sizes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelectableBox from 'UIComponents/button/UISelectableBox';
import Small from 'UIComponents/elements/Small';
import { H4 } from 'UIComponents/elements/headings';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIRibbon from 'UIComponents/ribbon/UIRibbon';
import UIStatusTag from 'UIComponents/tag/UIStatusTag';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { Flex } from 'foundations-components/components/Flex';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { getIsNewOffering } from 'marketplace-ui-common/offerings/utils';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUISelectableBox = styled(UISelectableBox).withConfig({
  displayName: "EcosystemAppCard__StyledUISelectableBox",
  componentId: "sc-81wr2-0"
})(["&:not(:hover){border-color:transparent;}padding:", " !important;text-align:left !important;width:100% !important;height:100% !important;> div:first-child{height:100%;}"], CARD_PADDING_COMPACT);
/**
 * [Storybook](https://private.hubteamqa.com/trellis-ecosystem-storybook/?path=/docs/trellis-ecosystem-ecosystemappcard)
 * */
export function EcosystemAppCard({
  appName,
  certified,
  customBody,
  customFooter,
  firstPublishedAt,
  iconUrl,
  installCount,
  installed,
  layout,
  onClick,
  providerName,
  ribbon,
  tagline
}) {
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [ribbon && /*#__PURE__*/_jsx(UIRibbon, {
      use: ribbon.use,
      children: ribbon.text
    }), /*#__PURE__*/_jsx(StyledUISelectableBox, {
      "data-test-id": "ecosystem-app-card",
      onClick: onClick
      // prevent persistent selected effect when clicked
      ,
      selected: false
      // if there's no onClick, we should not keyboard focusable, since most
      // likely we are already wrapped in a focusable element, and this would
      // create duplicate keyboard navigation
      ,
      tabIndex: onClick ? 0 : -1,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        blockSize: "100%",
        gap: "400",
        flexDirection: layout === 'grid' ? 'column' : 'row',
        paddingBlockStart: ribbon ? '250' : undefined,
        children: [/*#__PURE__*/_jsx(UIImage, {
          src: iconUrl,
          width: 40,
          height: 40,
          alt: appName
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "400",
          blockSize: "100%",
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(Flex, {
              alignItems: "end",
              gap: "200",
              children: /*#__PURE__*/_jsxs(H4, {
                className: "m-bottom-0",
                children: [appName, "\xA0", certified && /*#__PURE__*/_jsx(CertifiedBadge, {})]
              })
            }), /*#__PURE__*/_jsxs(Flex, {
              gap: "200",
              children: [providerName && /*#__PURE__*/_jsx(ProviderName, {
                providerName: providerName
              }), !installed && /*#__PURE__*/_jsx(InstallCount, {
                firstPublishedAt: firstPublishedAt,
                installCount: installCount
              })]
            })]
          }), tagline, customBody, installed && /*#__PURE__*/_jsx(InstalledTag, {}), customFooter]
        })]
      })
    })]
  });
}
function CertifiedBadge() {
  return /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "trellisEcosystem.appCard.certified"
    }),
    children: /*#__PURE__*/_jsx(UIIcon, {
      color: LORAX,
      "data-test-id": "certified-badge",
      name: "hubspotCertification",
      size: 20,
      style: {
        display: 'inline',
        verticalAlign: '1px'
      }
    })
  });
}
function ProviderName({
  providerName
}) {
  return /*#__PURE__*/_jsx(Small, {
    style: {
      color: SLINKY
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "trellisEcosystem.appCard.providerName",
      options: {
        providerName
      }
    })
  });
}
function InstallCount({
  firstPublishedAt,
  installCount
}) {
  if (firstPublishedAt && getIsNewOffering(firstPublishedAt)) {
    return /*#__PURE__*/_jsx(Small, {
      style: {
        color: SLINKY
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'trellisEcosystem.appCard.new'
      })
    });
  }
  if (installCount === undefined) return null;
  return /*#__PURE__*/_jsx(Small, {
    style: {
      color: SLINKY
    },
    children: formatInstalls(installCount)
  });
}
function InstalledTag() {
  return /*#__PURE__*/_jsx(UIStatusTag, {
    use: "success",
    className: "m-top-auto",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "trellisEcosystem.appCard.installed"
    })
  });
}